
import { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useIsMobile } from './use-mobile';

export function useMapEvents(
  mapContainer: React.RefObject<HTMLDivElement>,
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapZoomLevel: 'country' | 'region' | 'city'
) {
  const { isMobile } = useIsMobile();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Handle map resize on container size changes
  useEffect(() => {
    const observeResize = () => {
      if (!mapContainer.current) return;
      
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height
          });
          
          if (map.current) {
            map.current.resize();
          }
        }
      });
      
      observer.observe(mapContainer.current);
      
      return () => {
        observer.disconnect();
      };
    };
    
    const resizeObserver = observeResize();
    
    return () => {
      if (resizeObserver) {
        resizeObserver();
      }
    };
  }, [mapContainer, map]);
  
  // Handle window orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      if (map.current) {
        setTimeout(() => {
          map.current?.resize();
        }, 200);
      }
    };
    
    if (isMobile) {
      window.addEventListener('orientationchange', handleOrientationChange);
    }
    
    return () => {
      if (isMobile) {
        window.removeEventListener('orientationchange', handleOrientationChange);
      }
    };
  }, [isMobile, map]);
  
  return dimensions;
}
